"use client";

import React, { ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface SafeErrorBoundaryProps {
  children: ReactNode;
}

interface SafeErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * A safe error boundary that's compatible with React 19 and Next.js 15
 * Avoids triggering subscription errors during initialization
 */
export class SafeErrorBoundary extends React.Component<
  SafeErrorBoundaryProps,
  SafeErrorBoundaryState
> {
  constructor(props: SafeErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<SafeErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error("[SafeErrorBoundary] Error caught:", error);
    console.error("[SafeErrorBoundary] Error info:", errorInfo);
    
    // Update state with error info
    this.setState({
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
          <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-2xl w-full">
            <h1 className="text-2xl font-bold mb-4 text-red-600">
              Oops! Something went wrong
            </h1>
            <div className="mb-4 p-4 bg-red-50 rounded border border-red-200">
              <p className="text-sm font-mono text-red-800 break-words">
                {this.state.error.message}
              </p>
            </div>
            {this.state.errorInfo && (
              <details className="mb-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                  Show error details
                </summary>
                <pre className="mt-2 p-4 bg-gray-50 rounded text-xs overflow-auto max-h-64">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <p className="mb-6 text-gray-600">
              We're sorry for the inconvenience. Please try refreshing the page.
            </p>
            <Button
              onClick={this.handleReset}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SafeErrorBoundary;
